import React, { useState, useRef, useEffect } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import SuggestionButtons from './SuggestionButtons';
import BusinessCard from './BusinessCard';
import { sendChatMessage } from '../services/api';
import './ChatInterface.css';

function ChatInterface({ 
  conversationId, 
  community, 
  currentBusiness, 
  contextDepth,
  onBusinessMention,
  onDeepDive 
}) {
  const [messages, setMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: currentBusiness 
        ? `Hi! I'm here to help you learn about ${currentBusiness.name}. What would you like to know?`
        : `Welcome to ${community?.name}! I can help you discover local businesses, find services, and answer questions about our community. What are you looking for today?`,
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Listen for quick query events from the landing page
    const handleQuickQuery = (event) => {
      if (event.detail?.query) {
        handleSendMessage(event.detail.query);
      }
    };

    window.addEventListener('quickQuery', handleQuickQuery);
    return () => window.removeEventListener('quickQuery', handleQuickQuery);
  }, []);

  useEffect(() => {
    // Update welcome message when context changes
    if (currentBusiness) {
      addMessage({
        role: 'assistant',
        content: `Now focusing on ${currentBusiness.name}. I can tell you about their menu, hours, reviews, and more. What would you like to know?`,
        metadata: { businessCard: currentBusiness }
      });
    }
  }, [currentBusiness]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = (message) => {
    const newMessage = {
      id: Date.now().toString(),
      timestamp: new Date(),
      ...message
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSendMessage = async (text) => {
    // Add user message
    addMessage({
      role: 'user',
      content: text
    });

    setIsLoading(true);

    try {
      const response = await sendChatMessage({
        message: text,
        conversationId,
        businessSlug: currentBusiness?.slug,
        contextDepth
      });

      // Add AI response
      addMessage({
        role: 'assistant',
        content: response.text,
        metadata: response.metadata
      });

      // Update suggestions
      setSuggestions(response.suggestions || []);

      // Check for business mentions
      if (response.mentionedBusinesses) {
        response.mentionedBusinesses.forEach(business => {
          if (business.slug !== currentBusiness?.slug) {
            onBusinessMention(business);
          }
        });
      }

    } catch (error) {
      addMessage({
        role: 'system',
        content: 'Sorry, I had trouble processing that. Please try again.',
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleImageUpload = async (file) => {
    // Handle image uploads for menu photos, etc.
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch('/api/vision', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      addMessage({
        role: 'assistant',
        content: data.response,
        metadata: { imageAnalysis: true }
      });
      
    } catch (error) {
      console.error('Image upload failed:', error);
    }
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        {currentBusiness ? (
          <BusinessCard 
            business={currentBusiness} 
            compact={true}
            onClose={() => onBusinessMention(null)}
          />
        ) : (
          <div className="context-info">
            <h2>Chat with {community?.name} AI</h2>
            <p>{messages.length - 1} messages in this conversation</p>
          </div>
        )}
      </div>

      <MessageList 
        messages={messages}
        isLoading={isLoading}
        onBusinessClick={onBusinessMention}
        onDeepDive={onDeepDive}
      />
      
      <div ref={messagesEndRef} />

      {suggestions.length > 0 && (
        <SuggestionButtons 
          suggestions={suggestions}
          onSuggestionClick={handleSuggestionClick}
        />
      )}

      <MessageInput 
        onSendMessage={handleSendMessage}
        onImageUpload={handleImageUpload}
        disabled={isLoading}
        placeholder={
          currentBusiness 
            ? `Ask about ${currentBusiness.name}...`
            : `Ask about ${community?.name}...`
        }
      />
    </div>
  );
}

export default ChatInterface;